import type { ReactNode } from "react";

export interface IconProps {
  size?: number;
  stroke?: number;
  className?: string;
}

function IconBase({ size = 20, stroke = 1.6, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {children}
    </svg>
  );
}

export function LeafIcon(props: IconProps) {
  return <IconBase {...props}><path d="M4 20c0-9 7-16 16-16 0 9-7 16-16 16Z" /><path d="M4 20 14 10" /></IconBase>;
}

export function PlusIcon(props: IconProps) {
  return <IconBase {...props}><path d="M12 5v14M5 12h14" /></IconBase>;
}

export function RainIcon(props: IconProps) {
  return <IconBase {...props}><path d="M7 16a4 4 0 0 1-.6-7.96 6 6 0 0 1 11.6 1.96A3.5 3.5 0 0 1 17.5 16H7Z" /><path d="M9 19l-1 2M13 19l-1 2M17 19l-1 2" /></IconBase>;
}

export function CameraIcon(props: IconProps) {
  return <IconBase {...props}><path d="M3 8h3l2-3h8l2 3h3v11H3z" /><circle cx="12" cy="13" r="4" /></IconBase>;
}

export function DropletIcon(props: IconProps) {
  return <IconBase {...props}><path d="M12 3s6 7 6 12a6 6 0 0 1-12 0c0-5 6-12 6-12Z" /></IconBase>;
}

export function DropletOffIcon(props: IconProps) {
  return <IconBase {...props}><path d="M12 3s6 7 6 12a6 6 0 0 1-12 0c0-5 6-12 6-12Z" /><path d="M5 5l14 14" /></IconBase>;
}

export function SunIcon(props: IconProps) {
  return <IconBase {...props}><circle cx="12" cy="12" r="4" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4" /></IconBase>;
}

export function WateringCanIcon(props: IconProps) {
  return <IconBase {...props}><path d="M9 10h11v8a3 3 0 0 1-3 3h-5a3 3 0 0 1-3-3v-8z" /><path d="M9 10l-6-3" /><path d="M13 10V7a2 2 0 0 1 4 0v3" /></IconBase>;
}

export function BackIcon(props: IconProps) {
  return <IconBase {...props}><path d="M15 6l-6 6 6 6" /></IconBase>;
}

export function ArrowUpIcon(props: IconProps) {
  return <IconBase {...props}><path d="M12 19V5M5 12l7-7 7 7" /></IconBase>;
}

export function ArrowDownIcon(props: IconProps) {
  return <IconBase {...props}><path d="M12 5v14M5 12l7 7 7-7" /></IconBase>;
}

export function ArrowRightIcon(props: IconProps) {
  return <IconBase {...props}><path d="M5 12h14M12 5l7 7-7 7" /></IconBase>;
}

export function CheckIcon(props: IconProps) {
  return <IconBase {...props}><path d="M5 12l5 5 9-11" /></IconBase>;
}

export function XIcon(props: IconProps) {
  return <IconBase {...props}><path d="M6 6l12 12M18 6 6 18" /></IconBase>;
}

export function SparkleIcon(props: IconProps) {
  return <IconBase {...props}><path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" /><path d="M20 2v4" /><path d="M22 4h-4" /><circle cx="4" cy="20" r="2" /></IconBase>;
}

export function EditIcon(props: IconProps) {
  return <IconBase {...props}><path d="M4 20h4l11-11-4-4L4 16zM14 6l4 4" /></IconBase>;
}

export function EllipsisIcon(props: IconProps) {
  return <IconBase {...props}><path d="M6 12h.01M12 12h.01M18 12h.01" /></IconBase>;
}

export function FlashIcon(props: IconProps) {
  return <IconBase {...props}><path d="M13 3 5 14h6l-1 7 8-11h-6z" /></IconBase>;
}

export function ThermoIcon(props: IconProps) {
  return <IconBase {...props}><path d="M10 14V5a2 2 0 0 1 4 0v9a4 4 0 1 1-4 0Z" /></IconBase>;
}

export function BeakerIcon(props: IconProps) {
  return <IconBase {...props}><path d="M9 3h6M10 3v7l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3" /></IconBase>;
}

export function PinIcon(props: IconProps) {
  return <IconBase {...props}><path d="M12 21s7-7 7-12a7 7 0 0 0-14 0c0 5 7 12 7 12Z" /><circle cx="12" cy="9" r="2.5" /></IconBase>;
}

export function UserIcon(props: IconProps) {
  return <IconBase {...props}><circle cx="12" cy="8" r="3.5" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></IconBase>;
}

export function LogoutIcon(props: IconProps) {
  return <IconBase {...props}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></IconBase>;
}

export function PlantIcon(props: IconProps) {
  return <IconBase {...props}><path d="M12 21V11" /><path d="M12 11c-3 0-5-2-5-6 3 0 5 2 5 6Z" /><path d="M12 14c0-3 2-5 5-5 0 3-2 5-5 5Z" /></IconBase>;
}

export function PotIcon(props: IconProps) {
  return <IconBase {...props}><path d="M5 10h14l-1.5 9.5a2 2 0 0 1-2 1.5H8.5a2 2 0 0 1-2-1.5L5 10Z" /><path d="M4 7h16" /><path d="M9 4h6" /></IconBase>;
}

export function GroupIcon(props: IconProps) {
  return <IconBase {...props}><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></IconBase>;
}

export function MistIcon(props: IconProps) {
  return <IconBase {...props}><path d="M9 5h6l-2 4 2 3h-6l2-3-2-4Z" /><path d="M5 16h2M11 16h2M17 16h2M7 20h2M13 20h2" /></IconBase>;
}

export function FlowerIcon(props: IconProps) {
  return <IconBase {...props}><circle cx="12" cy="12" r="2.5" /><path d="M12 2c-2 2-2 5 0 7M12 22c2-2 2-5 0-7M2 12c2-2 5-2 7 0M22 12c-2 2-5 2-7 0" /></IconBase>;
}

export function CheckCircleIcon(props: IconProps) {
  return <IconBase {...props}><circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" /></IconBase>;
}

export function AlertCircleIcon(props: IconProps) {
  return <IconBase {...props}><circle cx="12" cy="12" r="9" /><path d="M12 8v4" /><path d="M12 16h.01" /></IconBase>;
}
